import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PublicLayout } from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";

export default function Contact() {
  const { data: s } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const waNumber = (s?.whatsapp_number || "").replace(/[^0-9]/g, "");

  const submit = (e) => {
    e.preventDefault();
    const text = `Hi Saad IPTV,%0AName: ${form.name}%0AEmail: ${form.email}%0A${form.message}`;
    if (waNumber) window.open(`https://wa.me/${waNumber}?text=${text}`, "_blank");
    else toast.success("Thanks! We'll get back to you soon.");
  };

  const cards = [
    { icon: Mail, label: "Email", value: s?.contact_email },
    { icon: Phone, label: "Phone", value: s?.contact_phone },
    { icon: MapPin, label: "Location", value: s?.address },
  ].filter((c) => c.value);

  return (
    <PublicLayout>
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
        <div className="text-center mb-12">
          <p className="text-xs font-semibold uppercase tracking-widest text-purple-400 mb-3">Get in touch</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white">Contact us</h1>
          <p className="text-slate-400 mt-4 max-w-xl mx-auto">Questions about packages or setup? Our team replies fast on WhatsApp.</p>
        </div>
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            {cards.map((c) => (
              <div key={c.label} className="glass rounded-2xl p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600/30 to-blue-600/30 border border-white/10 flex items-center justify-center">
                  <c.icon className="w-5 h-5 text-neon-purple" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">{c.label}</div>
                  <div className="text-white font-medium">{c.value}</div>
                </div>
              </div>
            ))}
            {waNumber && (
              <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn">
                <Button className="w-full bg-[#25D366] hover:bg-[#1eb855] text-white border-0 py-6"><MessageCircle className="w-4 h-4 mr-2" /> Chat on WhatsApp</Button>
              </a>
            )}
          </div>
          <form onSubmit={submit} className="glass rounded-2xl p-7 space-y-4">
            <div>
              <Label className="text-slate-300">Name</Label>
              <Input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Email</Label>
              <Input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Message</Label>
              <Textarea data-testid="contact-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" />
            </div>
            <Button data-testid="contact-submit" type="submit" className="w-full glow-btn bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0">Send Message</Button>
          </form>
        </div>
      </section>
    </PublicLayout>
  );
}
